import { Box, Select, Stack, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { resolutionStatuses } from "../../../../utils/vars";
import {
  useSelectDataForMonitoring,
  useSelectFiltersForMonitoring,
} from "../../../../redux/features/reports/forMonitoringSlice";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import useForMonitoring from "./useForMonitoring.logic";
import { RESOLUTION_STATUS_OPTIONS } from "../constants";
import classes from "./forMonitoringTable.module.scss";
import { dateFormat, dateWithHourFormat } from "../../../../utils/dateFormat";

const COLUMN_COUNT = 7;

const ForMonitoringTable = () => {
  const { t } = useTranslation();
  const data = useSelectDataForMonitoring();
  const filters = useSelectFiltersForMonitoring();

  const {
    page,
    take,
    totalCount,
    isFetching,
    handlePageChange,
    handleTakeChange,
    handleFilterChange,
    handleRowClick,
  } = useForMonitoring();

  const onResolutionDateChange = ({ startDate, endDate }) => {
    handleFilterChange({
      resolutionDateFrom: startDate || "",
      resolutionDateTo: endDate || "",
    });
  };

  const onDueDateChange = ({ startDate, endDate }) => {
    handleFilterChange({
      dueDateFrom: startDate || "",
      dueDateTo: endDate || "",
    });
  };

  const rows = data.map((row, i) => (
    <Table.Tr
      key={`${row.flagId}-${i}`}
      onClick={() => handleRowClick(row.flagId)}
    >
      <Table.Td>{row.flagId}</Table.Td>
      <Table.Td>{resolutionStatuses[row.resolution] || ""}</Table.Td>
      <Table.Td>
        {row.resolutionDate
          ? dayjs(row.resolutionDate).format(dateWithHourFormat)
          : ""}
      </Table.Td>
      <Table.Td>
        {row.dueDate ? dayjs(row.dueDate).format(dateFormat) : ""}
      </Table.Td>
      <Table.Td>{row.executionCheck}</Table.Td>
      <Table.Td>{row.tenant}</Table.Td>
      <Table.Td>{row.assignee}</Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Box className="table-panel">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["for-monitoring-table"]}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes["header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.flagId")}
                  </Text>
                  <ThrottledTextInput
                    size="xs"
                    delay={600}
                    value={filters.flagId}
                    onThrottledChange={(val) =>
                      handleFilterChange({ flagId: val })
                    }
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.resolution")}
                  </Text>
                  <Select
                    size="xs"
                    clearable
                    data={RESOLUTION_STATUS_OPTIONS}
                    value={filters.resolutionStatus || null}
                    onChange={(val) =>
                      handleFilterChange({ resolutionStatus: val || "" })
                    }
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["date-header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.resolutionDate")}
                  </Text>
                  <DateSearchFilter
                    placeholder={t("reportsForMonitoring.resolutionDate")}
                    onChange={onResolutionDateChange}
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["date-header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.dueDate")}
                  </Text>
                  <DateSearchFilter
                    placeholder={t("reportsForMonitoring.dueDate")}
                    onChange={onDueDateChange}
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.executionCheck")}
                  </Text>
                  <ThrottledTextInput
                    size="xs"
                    delay={600}
                    value={filters.executionCheck}
                    onThrottledChange={(val) =>
                      handleFilterChange({ executionCheck: val })
                    }
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.tenant")}
                  </Text>
                  <ThrottledTextInput
                    size="xs"
                    delay={600}
                    value={filters.tenant}
                    onThrottledChange={(val) =>
                      handleFilterChange({ tenant: val })
                    }
                  />
                </Stack>
              </Table.Th>

              <Table.Th className={classes["header-cell"]}>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("reportsForMonitoring.assignee")}
                  </Text>
                  <ThrottledTextInput
                    size="xs"
                    delay={600}
                    value={filters.assignee}
                    onThrottledChange={(val) =>
                      handleFilterChange({ assignee: val })
                    }
                  />
                </Stack>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          {isFetching ? (
            <TableSkeleton cols={COLUMN_COUNT} length={take} />
          ) : data.length === 0 ? (
            <Table.Tbody>
              <TableNoResults colSpan={COLUMN_COUNT} />
            </Table.Tbody>
          ) : (
            <Table.Tbody pos="relative">{rows}</Table.Tbody>
          )}
        </Table>
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={take}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleTakeChange}
        />
      )}
    </>
  );
};

export default ForMonitoringTable;
